/**
 * ClipboardService – manages copy, paste, and undo operations across grids.
 * React port of Angular ClipboardService.
 */
import type { GridApi } from 'ag-grid-community';
import { parseCellKey, isNonPrimaryBeforeSplit } from '../helpers/gridUtilities';
import * as GridDataService from './gridDataService';
import { PLANLEVEL_GROUND } from '../constants';

// ── Models ─────────────────────────────────────────────────────────

export interface ClipboardCellData {
  field: string;
  value: number;
  endingSeasonValue?: number;
  latestSeasonValue?: number;
  isMultiSeason: boolean;
  rowIndex: number;
  kpiId: string;
}

export interface ClipboardData {
  cells: ClipboardCellData[];
  isMainMarket: boolean;
  timestamp: number;
}

export interface UndoAction {
  type: 'edit' | 'paste';
  isMainMarket: boolean;
  previousState: Map<string, { value: number | null; endingSeasonValue?: number | null }>;
  affectedFields: string[];
  affectedRows: number[];
  timestamp: number;
}

// ── Service class ──────────────────────────────────────────────────

export class ClipboardService {
  private clipboardData: ClipboardData | null = null;
  private undoStack: UndoAction[] = [];
  private readonly maxUndoStackSize: number;

  constructor(maxUndoStackSize = 15) {
    this.maxUndoStackSize = maxUndoStackSize;
  }

  // ── Copy ───────────────────────────────────────────────────────

  /**
   * Copy selected cells.
   * @returns Number of cells copied, or -1 if validation fails (mixed time-range).
   */
  copy(
    selectedCells: Set<string>,
    gridApi: GridApi,
    isMainMarket: boolean,
    weekToSeasonMap: Map<string, string[]>,
  ): number {
    if (selectedCells.size === 0) return 0;

    if (!this.validateTimeRangeConsistency(selectedCells, gridApi)) return -1;

    const cells = this.extractCellsData(selectedCells, gridApi, weekToSeasonMap);
    this.clipboardData = { cells, isMainMarket, timestamp: Date.now() };
    return cells.length;
  }

  // ── Paste ──────────────────────────────────────────────────────

  paste(
    focusedCellKey: string | null,
    gridApi: GridApi,
    isMainMarket: boolean,
    weekToSeasonMap: Map<string, string[]>,
    cellFieldNameToWeekMap: Map<string, string>,
  ): { pastedCount: number; updatedData: Map<string, { rowIndex: number; field: string; value: number; endingSeasonValue?: number }> } | null {
    if (!this.clipboardData || !focusedCellKey) return null;

    const targetCells = this.generateTargetCells(focusedCellKey, gridApi);
    if (!targetCells) return null;

    if (!this.validateTargetCellsEditable(targetCells, gridApi, cellFieldNameToWeekMap)) {
      return { pastedCount: -1, updatedData: new Map() };
    }

    return this.executePaste(targetCells, gridApi, isMainMarket, weekToSeasonMap, cellFieldNameToWeekMap);
  }

  // ── Undo ───────────────────────────────────────────────────────

  undo(): UndoAction | null {
    return this.undoStack.length === 0 ? null : this.undoStack.pop() ?? null;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  getPreviousState(action: UndoAction): Map<string, { value: number | null; endingSeasonValue?: number | null }> {
    return action.previousState;
  }

  // ── Edit tracking ──────────────────────────────────────────────

  addEditAction(
    isMainMarket: boolean,
    field: string,
    rowIndex: number,
    previousValue: number | null,
    previousEndingSeasonValue?: number | null,
  ): void {
    const previousState = new Map<string, { value: number | null; endingSeasonValue?: number | null }>();
    previousState.set(`${rowIndex}_${field}`, { value: previousValue, endingSeasonValue: previousEndingSeasonValue });

    this.addToUndoStack({
      type: 'edit',
      isMainMarket,
      previousState,
      affectedFields: [field],
      affectedRows: [rowIndex],
      timestamp: Date.now(),
    });
  }

  // ── Housekeeping ───────────────────────────────────────────────

  clearClipboard(): void {
    this.clipboardData = null;
  }

  clearUndoStack(): void {
    this.undoStack = [];
  }

  getClipboardData(): ClipboardData | null {
    return this.clipboardData;
  }

  getUndoStackSize(): number {
    return this.undoStack.length;
  }

  // ── Private helpers ────────────────────────────────────────────

  private validateTimeRangeConsistency(selectedCells: Set<string>, gridApi: GridApi): boolean {
    const timeLevels = new Set<string>();
    for (const cellKey of selectedCells) {
      const parsed = parseCellKey(cellKey);
      if (!parsed) continue;
      const rowNode = gridApi.getDisplayedRowAtIndex(parsed.rowIndex);
      if (!rowNode?.data) continue;
      const tl = rowNode.data.timeLevel as string | undefined;
      if (tl) {
        timeLevels.add(tl);
        if (timeLevels.size > 1) return false;
      }
    }
    return true;
  }

  private extractCellsData(
    selectedCells: Set<string>,
    gridApi: GridApi,
    weekToSeasonMap: Map<string, string[]>,
  ): ClipboardCellData[] {
    const cells: ClipboardCellData[] = [];
    const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();

    for (const cellKey of selectedCells) {
      const parsed = parseCellKey(cellKey);
      if (!parsed) continue;
      const { rowIndex, fieldName } = parsed;
      const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
      if (!rowNode?.data) continue;

      const week = cellFieldNameToWeekMap.get(fieldName);
      if (week === undefined) continue;

      const isMultiSeason = (weekToSeasonMap.get(week)?.length ?? 0) === 2;
      const timeLevel = rowNode.data.timeLevel as string;
      const rawValue = rowNode.data[fieldName];
      const parsedValue = this.parseCellValue(rawValue, timeLevel);

      const cellData: ClipboardCellData = {
        field: fieldName,
        value: parsedValue,
        isMultiSeason,
        rowIndex,
        kpiId: (rowNode.data.id as string) ?? (rowNode.data.kpi as string),
      };

      if (isMultiSeason) {
        const esField = `${fieldName}_es`;
        const rawEs = rowNode.data[esField];
        cellData.endingSeasonValue = this.parseCellValue(rawEs, timeLevel);
        cellData.latestSeasonValue = cellData.value - (cellData.endingSeasonValue ?? 0);
      }

      cells.push(cellData);
    }
    return cells;
  }

  private parseCellValue(rawValue: unknown, timeLevel: string): number {
    if (timeLevel === 'Week' || typeof rawValue === 'number') return Number(rawValue ?? 0);
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (trimmed === '' || trimmed === '-') return 0;
      const parenIdx = trimmed.indexOf('(');
      if (parenIdx !== -1) {
        const plan = trimmed.substring(0, parenIdx).trim();
        return plan === '' || plan === '-' ? 0 : Number(plan);
      }
      return Number(trimmed);
    }
    return 0;
  }

  private generateTargetCells(focusedCellKey: string, gridApi: GridApi): string[] | null {
    const start = parseCellKey(focusedCellKey);
    if (!start) return null;
    const allCols = gridApi.getAllDisplayedColumns() ?? [];
    const startColIdx = allCols.findIndex((c) => c.getColId() === start.fieldName);
    if (startColIdx === -1) return null;

    const sourceCells = this.clipboardData?.cells ?? [];
    const targets: string[] = [];
    for (let i = 0; i < sourceCells.length; i++) {
      const idx = startColIdx + i;
      if (idx < allCols.length) {
        targets.push(`${start.rowIndex}_${allCols[idx].getColId()}`);
      }
    }
    return targets;
  }

  private validateTargetCellsEditable(
    targetCells: string[],
    gridApi: GridApi,
    cellFieldNameToWeekMap: Map<string, string>,
  ): boolean {
    const timeline = GridDataService.getTimelineMap();
    const currentWeek = timeline?.currentWeek ?? '';

    for (const cellKey of targetCells) {
      const parsed = parseCellKey(cellKey);
      if (!parsed) continue;
      const rowNode = gridApi.getDisplayedRowAtIndex(parsed.rowIndex);
      if (!rowNode?.data) continue;
      if (rowNode.data.isCalculated) return false;
      if (rowNode.data.planLevel === PLANLEVEL_GROUND) return false;
      const week = cellFieldNameToWeekMap.get(parsed.fieldName) ?? parsed.fieldName;
      if (currentWeek && week < currentWeek) return false;
      if (isNonPrimaryBeforeSplit(rowNode.data as Record<string, unknown>, week, GridDataService.getWeekToSeasonMap())) return false;
    }
    return true;
  }

  private executePaste(
    targetCells: string[],
    gridApi: GridApi,
    isMainMarket: boolean,
    weekToSeasonMap: Map<string, string[]>,
    cellFieldNameToWeekMap: Map<string, string>,
  ): { pastedCount: number; updatedData: Map<string, { rowIndex: number; field: string; value: number; endingSeasonValue?: number }> } {
    const previousState = this.capturePreviousState(targetCells, gridApi);
    const updatedData = new Map<string, { rowIndex: number; field: string; value: number; endingSeasonValue?: number }>();
    const affectedRows: number[] = [];
    const affectedFields: string[] = [];
    let pastedCount = 0;
    const sourceCells = this.clipboardData?.cells ?? [];

    for (const [index, targetCellKey] of targetCells.entries()) {
      const result = this.pasteSingleCell(targetCellKey, sourceCells[index], gridApi, weekToSeasonMap, cellFieldNameToWeekMap);
      if (result) {
        updatedData.set(`${result.rowIndex}_${result.field}`, result);
        if (!affectedRows.includes(result.rowIndex)) affectedRows.push(result.rowIndex);
        if (!affectedFields.includes(result.field)) affectedFields.push(result.field);
        pastedCount++;
      }
    }

    if (pastedCount > 0) {
      this.addToUndoStack({
        type: 'paste',
        isMainMarket,
        previousState,
        affectedFields,
        affectedRows,
        timestamp: Date.now(),
      });
    }

    return { pastedCount, updatedData };
  }

  private pasteSingleCell(
    targetCellKey: string,
    sourceCell: ClipboardCellData,
    gridApi: GridApi,
    weekToSeasonMap: Map<string, string[]>,
    cellFieldNameToWeekMap: Map<string, string>,
  ): { rowIndex: number; field: string; value: number; endingSeasonValue?: number } | null {
    const parsed = parseCellKey(targetCellKey);
    if (!parsed) return null;
    const { rowIndex, fieldName } = parsed;
    const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
    if (!rowNode?.data) return null;

    // Skip if both empty
    const isSourceEmpty = !sourceCell.value || sourceCell.value === 0;
    const targetVal = rowNode.data[fieldName] ?? 0;
    const targetEs = rowNode.data[`${fieldName}_es`] ?? 0;
    if (isSourceEmpty && targetVal === 0 && targetEs === 0) return null;

    const targetWeek = cellFieldNameToWeekMap.get(fieldName) ?? fieldName;
    const isTargetMulti = (weekToSeasonMap.get(targetWeek)?.length ?? 0) === 2;
    const result = this.applyPasteLogic(sourceCell, isTargetMulti);
    return result ? { rowIndex, field: fieldName, ...result } : null;
  }

  /**
   * 4-case paste logic:
   * 1. Single→Single: direct value
   * 2. Single→Multi: value goes to ending-season
   * 3. Multi→Single: combined value
   * 4. Multi→Multi: old→old, latest→latest
   */
  private applyPasteLogic(
    src: ClipboardCellData,
    isTargetMulti: boolean,
  ): { value: number; endingSeasonValue?: number } | null {
    if (!src.isMultiSeason && !isTargetMulti) {
      return { value: src.value };
    }
    if (!src.isMultiSeason && isTargetMulti) {
      return { value: src.value, endingSeasonValue: src.value };
    }
    if (src.isMultiSeason && !isTargetMulti) {
      return { value: src.value };
    }
    if (src.isMultiSeason && isTargetMulti) {
      return {
        value: (src.endingSeasonValue ?? 0) + (src.latestSeasonValue ?? 0),
        endingSeasonValue: src.endingSeasonValue ?? 0,
      };
    }
    return null;
  }

  private capturePreviousState(
    cellKeys: string[],
    gridApi: GridApi,
  ): Map<string, { value: number | null; endingSeasonValue?: number | null }> {
    const state = new Map<string, { value: number | null; endingSeasonValue?: number | null }>();
    for (const cellKey of cellKeys) {
      const parsed = parseCellKey(cellKey);
      if (!parsed) continue;
      const rowNode = gridApi.getDisplayedRowAtIndex(parsed.rowIndex);
      if (!rowNode?.data) continue;
      state.set(cellKey, {
        value: rowNode.data[parsed.fieldName] ?? null,
        endingSeasonValue: rowNode.data[`${parsed.fieldName}_es`] ?? null,
      });
    }
    return state;
  }

  private addToUndoStack(action: UndoAction): void {
    this.undoStack.push(action);
    if (this.undoStack.length > this.maxUndoStackSize) this.undoStack.shift();
  }
}
